import { CustomAttributes } from "../../pojo/CustomAttributes";
import { AnswerType, type IngestionFieldDefinition } from "./data.ingestion.service";

/** Matches `ingestion_field_definition.info_text` nvarchar(4000). */
export const INGESTION_INFO_TEXT_MAX_LENGTH = 4000;
export const INGESTION_STRING_MAX_LENGTH = 255;
export const INGESTION_LARGE_STRING_MAX_LENGTH = 4000;
export const INGESTION_CUSTOM_ATTR_TEXT_MAX_LENGTH = 80;
export const INGESTION_URL_MAX_LENGTH = 250;

function formatCharacterCount(count: number): string {
    return count === 1 ? "1 character" : `${count} characters`;
}

/** Text URL custom attrs use a plain input; image/file URL attrs use upload UI instead. */
function isTextUrlCustomAttribute(fieldDef: IngestionFieldDefinition): boolean {
    return fieldDef.custAtrbId === -1 || fieldDef.custAtrbId === -2;
}

export function isOverCharLimit(value: string | undefined, maxLength: number): boolean {
    return (value ?? "").length > maxLength;
}

export function formatCharCountRemaining(
    value: string | undefined,
    maxLength: number,
    includeFormatting = false,
): string {
    const remaining = maxLength - (value ?? "").length;
    const suffix = includeFormatting ? " (includes formatting)" : "";
    if (remaining < 0) {
        return `${formatCharacterCount(Math.abs(remaining))} over limit${suffix}`;
    }
    return `${formatCharacterCount(remaining)} remaining${suffix}`;
}

export function resolveFieldDefForLength(fieldDef: IngestionFieldDefinition): IngestionFieldDefinition {
    if (
        (fieldDef.fieldType === "CUSTOM_ATTRIBUTE" || fieldDef.fieldType === "ORG_CUSTOM_ATTRIBUTE") &&
        fieldDef.answerType === AnswerType.VARIANT &&
        fieldDef.custAtrb?.atrbDataType
    ) {
        const resolvedAnswerType = AnswerType.fromAtrbDataType(fieldDef.custAtrb.atrbDataType) ?? fieldDef.answerType;
        return { ...fieldDef, answerType: resolvedAnswerType };
    }
    return fieldDef;
}

export function getEffectiveMaxLength(fieldDef: IngestionFieldDefinition): number | undefined {
    const resolved = resolveFieldDefForLength(fieldDef);

    if (resolved.answerType === AnswerType.INTEGER || resolved.answerType === AnswerType.FLOAT) {
        return undefined;
    }

    if (resolved.maxLength != null) {
        return resolved.maxLength;
    }

    if (isTextUrlCustomAttribute(resolved)) {
        return INGESTION_URL_MAX_LENGTH;
    }

    if (resolved.custAtrb?.atrbDataType === CustomAttributes.type.Text) {
        return INGESTION_CUSTOM_ATTR_TEXT_MAX_LENGTH;
    }

    if (
        resolved.fieldType === "RICH_TEXT" ||
        resolved.answerType === AnswerType.LARGE_STRING ||
        resolved.custAtrb?.atrbDataType === CustomAttributes.type.LongText
    ) {
        return INGESTION_LARGE_STRING_MAX_LENGTH;
    }

    if (resolved.fieldType === "TEXT" && resolved.answerType === AnswerType.STRING) {
        return INGESTION_STRING_MAX_LENGTH;
    }

    return undefined;
}

export function fieldUsesRichTextLength(fieldDef: IngestionFieldDefinition): boolean {
    const resolved = resolveFieldDefForLength(fieldDef);
    return (
        resolved.fieldType === "RICH_TEXT" ||
        (resolved.fieldType === "TEXT" && resolved.answerType === AnswerType.LARGE_STRING)
    );
}

export function shouldShowCharCount(fieldDef: IngestionFieldDefinition, readOnly: boolean): boolean {
    return !readOnly && getEffectiveMaxLength(fieldDef) != null;
}
